/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import InfoIcon from '@mui/icons-material/Info';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import React from "react";
import { InputFieldWrapperProps } from './IInputWrapperProps';

const InputFieldWrapper: React.FC<InputFieldWrapperProps> = ({
  label,
  required,
  info,
  placeholder,
  error,
  errorText,
  hint,
  children,
  ...rest
}) => {
  return (
    <FormControl fullWidth required={required} error={error} {...rest}>
      {/* Use Stack to stack label and hint with padding bottom for the entire block */}
      <Stack spacing={0.5} sx={{ paddingBottom: "8px" }}>
        {/* Render the FormLabel with the required asterisk and info icon */}
        <FormLabel
          sx={{
            display: "flex",
            alignItems: "center",
            "& .MuiFormLabel-asterisk": { display: "none" },
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}>*</span>}

          {/* Position the InfoIcon at the end */}
          {info && (
            <Tooltip title={info} arrow>
              <IconButton
                sx={{ padding: 0, marginLeft: "auto" }}
                aria-label="Information"
              >
                <InfoIcon fontSize="small" color="info" />
              </IconButton>
            </Tooltip>
          )}
        </FormLabel>

        {/* Optionally render the hint below the label */}
        {hint && (
          <div
            style={{
              fontSize: "0.875rem",
              color: "gray",
              fontStyle: "italic",
              marginTop: 0,
            }}
          >
            {hint}
          </div>
        )}
      </Stack>

      {/* Handle the children and apply logic for placeholder with select */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // If the child is a TextField, only pass the error prop to it
          if (child.type === TextField) {
            return React.cloneElement(child, {
              //@ts-ignore
              error
            });
          }

          // If the child is a Select component and there's a placeholder, add a MenuItem as the placeholder
          if (child.type === Select && placeholder) {
            return React.cloneElement(child, {
              //@ts-ignore
              children: [
                <MenuItem value="" key="placeholder" disabled>
                  <em>{placeholder}</em>
                </MenuItem>,
                ...React.Children.toArray(child.props.children),
              ],
              input: <OutlinedInput notched={false} />,
            });
          }

          return React.cloneElement(child, {
            //@ts-ignore
            placeholder: placeholder,
          });
        }

        return child;
      })}

      {/* Conditionally render error message only if error is true */}
      {error && errorText && (
        <FormHelperText sx={{ margin: "0px" }}>{errorText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default InputFieldWrapper;
